import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { EnrollmentModal } from "../EnrollmentModal";
import { Clock, Users, Star, Calendar, BookOpen, Palette, Microscope, Music, TreePine, Utensils, Heart } from 'lucide-react';
import { useState } from 'react';

const programs = [
  {
    title: "Creative Arts & Crafts",
    description: "Unleash creativity through painting, drawing, sculpting, and various craft projects that develop fine motor skills and artistic expression.",
    image: "https://images.unsplash.com/photo-1611085667203-7efa7c067bce?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMGFydCUyMGNyZWF0aXZlfGVufDF8fHx8MTc1NjkyNTg0MXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    duration: "45 mins",
    groupSize: "6-8 kids",
    rating: 4.9,
    age: "3-6 years",
    schedule: "Mon, Wed, Fri",
    icon: Palette,
    highlights: ["Fine motor development", "Creative expression", "Color theory", "Art history basics"]
  },
  {
    title: "Reading Adventures",
    description: "Build reading skills through interactive storytelling, phonics games, and guided reading sessions that make literacy fun and engaging.",
    image: "https://images.unsplash.com/photo-1549737221-bef65e2604a6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGlsZCUyMHJlYWRpbmclMjBib29rfGVufDF8fHx8MTc1NjkyNTg0MXww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    duration: "30 mins",
    groupSize: "4-6 kids",
    rating: 5.0,
    age: "4-7 years",
    schedule: "Daily",
    icon: BookOpen,
    highlights: ["Phonics mastery", "Reading comprehension", "Vocabulary building", "Story writing"]
  },
  {
    title: "Science Explorers",
    description: "Discover the wonders of science through hands-on experiments, observations, and interactive learning that sparks curiosity.",
    image: "https://images.unsplash.com/photo-1574132190990-cfd62178bb1c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxraWRzJTIwY2xhc3Nyb29tJTIwbGVhcm5pbmd8ZW58MXx8fHwxNzU2OTA3NDU3fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    duration: "60 mins",
    groupSize: "8-10 kids",
    rating: 4.8,
    age: "5-8 years",
    schedule: "Tue, Thu",
    icon: Microscope,
    highlights: ["STEM concepts", "Scientific method", "Nature exploration", "Critical thinking"]
  },
  {
    title: "Music & Movement",
    description: "Develop rhythm, coordination, and musical appreciation through singing, dancing, and playing simple instruments.",
    image: "https://images.unsplash.com/photo-1602863211761-ee8d16967a5a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMHBsYXlpbmclMjBlZHVjYXRpb25hbCUyMHRveXN8ZW58MXx8fHwxNzU2OTI1ODQwfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    duration: "40 mins",
    groupSize: "10-12 kids",
    rating: 4.9,
    age: "2-6 years",
    schedule: "Mon, Wed, Fri",
    icon: Music,
    highlights: ["Rhythm development", "Gross motor skills", "Musical theory", "Performance skills"]
  },
  {
    title: "Outdoor Adventures",
    description: "Connect with nature through outdoor exploration, gardening, nature walks, and environmental awareness activities.",
    image: "https://images.unsplash.com/photo-1595351298708-059bb646bc29?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxraWRzJTIwb3V0ZG9vciUyMHBsYXl8ZW58MXx8fHwxNzU2OTI1ODQxfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    duration: "60 mins",
    groupSize: "12-15 kids",
    rating: 4.7,
    age: "3-8 years",
    schedule: "Weather permitting",
    icon: TreePine,
    highlights: ["Nature appreciation", "Physical activity", "Environmental awareness", "Team building"]
  },
  {
    title: "Life Skills & Cooking",
    description: "Learn practical life skills through simple cooking activities, table manners, and basic household tasks adapted for children.",
    image: "https://images.unsplash.com/photo-1542810634-71277d95dcbb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxraWRzJTIwbGVhcm5pbmclMjB0b2dldGhlciUyMGNsYXNzcm9vbXxlbnwxfHx8fDE3NTY5MjU4OTF8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral",
    duration: "50 mins",
    groupSize: "6-8 kids",
    rating: 4.8,
    age: "4-8 years",
    schedule: "Tue, Fri",
    icon: Utensils,
    highlights: ["Independence skills", "Nutrition awareness", "Safety practices", "Cultural foods"]
  }
];

export function ProgramsPage() {
  const [isEnrollmentModalOpen, setIsEnrollmentModalOpen] = useState(false);

  return (
    <>
      <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-purple-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="bg-purple-100 text-purple-600 px-4 py-2 rounded-full text-sm font-medium">
              Our Programs
            </span>
            <h1 className="text-5xl font-bold text-gray-800 mt-4 mb-6">
              Comprehensive Learning Programs
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Discover our carefully designed programs that cater to different interests and developmental stages, 
              ensuring every child finds their passion and reaches their potential.
            </p>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <Card key={index} className="overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="relative">
                  <ImageWithFallback
                    src={program.image}
                    alt={program.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                    {program.age}
                  </div>
                </div>
                
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-orange-400 to-pink-400 rounded-full flex items-center justify-center">
                      <program.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">{program.title}</h3>
                  </div>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">{program.description}</p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center text-gray-500">
                        <Clock className="w-4 h-4 mr-2" />
                        {program.duration}
                      </div>
                      <div className="flex items-center text-gray-500">
                        <Users className="w-4 h-4 mr-2" />
                        {program.groupSize}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center text-gray-500">
                        <Calendar className="w-4 h-4 mr-2" />
                        {program.schedule}
                      </div>
                      <div className="flex items-center text-yellow-500">
                        <Star className="w-4 h-4 mr-1 fill-current" />
                        {program.rating}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-800 mb-2">Program Highlights:</h4>
                    <ul className="space-y-1">
                      {program.highlights.map((highlight, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-center">
                          <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-2"></div>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <Button 
                    className="w-full bg-orange-400 hover:bg-orange-500 text-white rounded-full"
                    onClick={() => setIsEnrollmentModalOpen(true)}
                  >
                    Enroll Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Program Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Program Benefits</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our programs are designed with your child's holistic development in mind.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Academic Excellence</h3>
                <p className="text-gray-600 leading-relaxed">
                  Strong foundation in core subjects with innovative teaching methods that make learning engaging and effective.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Social Skills</h3>
                <p className="text-gray-600 leading-relaxed">
                  Develop communication, teamwork, and leadership skills through collaborative activities and group projects.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Emotional Growth</h3>
                <p className="text-gray-600 leading-relaxed">
                  Build confidence, self-awareness, and emotional intelligence in a nurturing and supportive environment.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
    
    <EnrollmentModal 
      isOpen={isEnrollmentModalOpen} 
      onClose={() => setIsEnrollmentModalOpen(false)} 
    />
  </>
  );
}